import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Divider, Dropdown, Grid, Layout, Menu, Space, Tooltip, Typography, type MenuProps } from 'antd';
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  BellOutlined,
  CheckSquareOutlined,
  HistoryOutlined,
  ProjectOutlined,
  ContactsOutlined,
  DashboardOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  FilterOutlined,
  FormOutlined,
  FunnelPlotOutlined,
  InboxOutlined,
  LogoutOutlined,
  MailOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  TagsOutlined,
  ToolOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAuth } from '../auth/AuthProvider';
import { useInboxSummary } from '../features/email/api';
import { useCardScanSummary } from '../features/cardScans/api';
import { Badge } from 'antd';
import { useFeatures, useSettings, type FeatureKey } from '../features/settings/api';
import { setDateFormat } from '../utils/format';
import { GlobalSearch } from '../components/GlobalSearch';
import { useDocumentBranding, useDocumentTitle } from '../utils/document-head';
import { SETTINGS_SECTIONS, TOOLS_SECTIONS } from './sections';
import { DEFAULT_BRAND_NAME, SidebarBrand } from './SidebarBrand';
import { fileUrl } from '../api/client';
import { useScrollToTop } from './scroll-to-top';
import { ProjectScopeProvider } from '../features/projects/scope';
import { ProjectSwitcher } from '../components/ProjectSelect';

const { Sider, Header, Content } = Layout;

type MenuItem = NonNullable<MenuProps['items']>[number];

const SIDER_WIDTH = 256;
const SIDER_RAIL_WIDTH = 72;
const SIDER_COLLAPSED_KEY = 'crm.sider.collapsed';
/** Delay before the icon rail opens on hover, so passing the mouse across it does not flash the menu. */
const HOVER_OPEN_DELAY = 120;

/**
 * Sidebar that can be collapsed to an icon rail (remembered in localStorage).
 * While collapsed, hovering the rail opens the full menu on top of the page instead of pushing it aside.
 */
function useSiderState() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDER_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [hovered, setHovered] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    setHovered(false);
    try {
      localStorage.setItem(SIDER_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* storage unavailable: keep the in-memory state */
    }
  };
  const onMouseEnter = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHovered(true), HOVER_OPEN_DELAY);
  };
  const onMouseLeave = () => {
    window.clearTimeout(timer.current);
    setHovered(false);
  };
  return { collapsed, floating: collapsed && hovered, expanded: !collapsed || hovered, toggle, onMouseEnter, onMouseLeave };
}

const NAV_KEYS = [
  '/tasks/my', '/tasks/activity', '/tasks', '/settings', '/tools', '/contacts', '/organizations', '/pipeline', '/inbox', '/segments', '/lists', '/tags',
  '/campaigns', '/templates', '/cold-email', '/forms', '/account',
];
/** Tab title while a page has not named itself yet (loading, errors). Longest prefix first. */
const SECTION_TITLES: [string, string][] = [
  ...[...SETTINGS_SECTIONS, ...TOOLS_SECTIONS].map((s): [string, string] => [s.path, s.label]),
  ['/settings', 'Settings'], ['/tools', 'Tools'],
  ['/tasks/my', 'My tasks'], ['/tasks/activity', 'Task activity'], ['/tasks', 'Tasks'],
  ['/contacts', 'Contacts'], ['/organizations', 'Organizations'], ['/pipeline', 'Pipeline'], ['/deals', 'Pipeline'], ['/inbox', 'Inbox'],
  ['/segments', 'Segments'], ['/lists', 'Lists'], ['/tags', 'Tags'], ['/campaigns', 'Campaigns'],
  ['/templates', 'Templates'], ['/cold-email', 'Cold Emailing'], ['/forms', 'Forms'], ['/account', 'My account'],
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const features = useFeatures();
  const inboxOn = features.enabled('inbox');
  const pipelinesOn = features.enabled('pipelines');
  const campaignsOn = features.enabled('campaigns');
  const formsOn = features.enabled('forms');
  const tasksOn = features.enabled('tasks');
  const cardsOn = features.enabled('cardScanner');
  const cardSummary = useCardScanSummary(cardsOn);
  const cardsToCheck = cardsOn ? (cardSummary.data?.ready ?? 0) : 0;
  const inbox = useInboxSummary(inboxOn);
  const unread = inboxOn ? (inbox.data?.totalUnread ?? 0) : 0;
  const settings = useSettings();
  const companyName = settings.data?.companyName || DEFAULT_BRAND_NAME;
  useEffect(() => {
    setDateFormat(settings.data?.dateFormat);
  }, [settings.data?.dateFormat]);
  useDocumentBranding(companyName, unread, fileUrl(settings.data?.brandFaviconFileId));
  useScrollToTop();
  const sider = useSiderState();
  // false only once the screen is known to be narrower than a tablet
  const phone = Grid.useBreakpoint().md === false;
  const path = location.pathname;
  useDocumentTitle(path === '/' ? 'Dashboard' : SECTION_TITLES.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))?.[1], 0);

  // Feature entries follow the order set in Settings → Features: inside their group, and the Tasks and Marketing
  // groups swap places when their features do
  const rankKey = features.order.join();
  const items = useMemo<MenuProps['items']>(() => {
    const rank = (key: FeatureKey) => features.rank(key);
    const byRank = <T,>(entries: [FeatureKey, T][]) => entries.sort(([a], [b]) => rank(a) - rank(b)).map(([, item]) => item);
    const featureGroups = byRank<MenuItem | false>([
      [
        'tasks',
        tasksOn && {
          type: 'group',
          label: 'Tasks',
          children: [
            { key: '/tasks/my', icon: <CheckSquareOutlined />, label: <Link to="/tasks/my">My tasks</Link> },
            { key: '/tasks', icon: <ProjectOutlined />, label: <Link to="/tasks">Tasks</Link> },
            { key: '/tasks/activity', icon: <HistoryOutlined />, label: <Link to="/tasks/activity">Activity</Link> },
          ],
        },
      ],
      [
        rank('campaigns') < rank('forms') ? 'campaigns' : 'forms',
        // the whole group goes when Campaigns and Forms are both switched off
        (campaignsOn || formsOn) && {
          type: 'group',
          label: 'Marketing',
          children: byRank<MenuItem[]>([
            [
              'campaigns',
              campaignsOn
                ? [
                    { key: '/campaigns', icon: <MailOutlined />, label: <Link to="/campaigns">Campaigns</Link> },
                    { key: '/templates', icon: <FileTextOutlined />, label: <Link to="/templates">Templates</Link> },
                    { key: '/cold-email', icon: <ThunderboltOutlined />, label: <Link to="/cold-email">Cold Emailing</Link> },
                  ]
                : [],
            ],
            ['forms', formsOn ? [{ key: '/forms', icon: <FormOutlined />, label: <Link to="/forms">Forms</Link> }] : []],
          ]).flat(),
        },
      ],
    ]);
    return [
      {
        type: 'group',
        label: 'General',
        children: [
          { key: '/', icon: <DashboardOutlined />, label: <Link to="/">Dashboard</Link> },
          { key: '/contacts', icon: <ContactsOutlined />, label: <Link to="/contacts">Contacts</Link> },
          { key: '/organizations', icon: <ApartmentOutlined />, label: <Link to="/organizations">Organizations</Link> },
          ...byRank<MenuItem | false>([
            ['pipelines', pipelinesOn && { key: '/pipeline', icon: <FunnelPlotOutlined />, label: <Link to="/pipeline">Pipeline</Link> }],
            [
              'inbox',
              inboxOn && { key: '/inbox', icon: <InboxOutlined />, label: <Link to="/inbox">Inbox{unread > 0 && <Badge count={unread} size="small" style={{ marginLeft: 8 }} />}</Link> },
            ],
          ]),
        ].filter(Boolean),
      },
      featureGroups[0],
      {
        type: 'group',
        label: 'Audience',
        children: [
          { key: '/segments', icon: <FilterOutlined />, label: <Link to="/segments">Segments</Link> },
          { key: '/lists', icon: <UnorderedListOutlined />, label: <Link to="/lists">Lists</Link> },
          { key: '/tags', icon: <TagsOutlined />, label: <Link to="/tags">Tags</Link> },
        ],
      },
      featureGroups[1],
      {
        type: 'group',
        label: 'Workspace',
        children: [
          {
            key: '/tools',
            icon: <ToolOutlined />,
            // cards waiting to be checked under Tools → Scan cards
            label: <Link to="/tools">Tools{cardsToCheck > 0 && <Badge count={cardsToCheck} size="small" color="#2F5BEA" style={{ marginLeft: 8 }} />}</Link>,
          },
          { key: '/settings', icon: <SettingOutlined />, label: <Link to="/settings">Settings</Link> },
        ],
      },
    ].filter(Boolean) as MenuProps['items'];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rankKey stands for the order
  }, [unread, inboxOn, pipelinesOn, campaignsOn, formsOn, tasksOn, cardsOn, cardsToCheck, rankKey]);

  const selected = useMemo(() => {
    const path = location.pathname.startsWith('/deals') ? '/pipeline' : location.pathname;
    return [NAV_KEYS.find((k) => path.startsWith(k)) ?? '/'];
  }, [location.pathname]);

  const userMenu: MenuProps['items'] = [
    { key: 'account', icon: <UserOutlined />, label: 'My account', onClick: () => navigate('/account') },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', onClick: () => void logout() },
  ];

  // Scan cards is used on a phone: there it gets the whole screen, with a slim bar back to the CRM
  if (phone && TOOLS_SECTIONS.some((s) => s.phoneFullScreen && (path === s.path || path.startsWith(`${s.path}/`)))) {
    return (
      <ProjectScopeProvider>
        <div className="phone-shell">
          <header className="phone-shell-bar">
            <Link to="/" className="phone-shell-home">
              <ArrowLeftOutlined /> {companyName}
            </Link>
            <Dropdown menu={{ items: userMenu }} trigger={['click']} placement="bottomRight">
              <Button type="text" shape="circle" aria-label="Account menu" icon={<Avatar size={32} src={fileUrl(user?.photoFileId)} style={{ background: '#EEF2FE', color: '#2F5BEA', fontWeight: 600 }}>{user?.name?.[0]?.toUpperCase()}</Avatar>} />
            </Dropdown>
          </header>
          <Outlet />
        </div>
      </ProjectScopeProvider>
    );
  }

  return (
    <ProjectScopeProvider>
    <Layout className="app-shell">
      <div className="app-sider-slot" style={{ width: sider.collapsed ? SIDER_RAIL_WIDTH : SIDER_WIDTH }}>
        <Sider
          // Only the width changes: collapsing the Sider would also switch the Menu to its collapsed layout and shift every row
          width={sider.expanded ? SIDER_WIDTH : SIDER_RAIL_WIDTH}
          trigger={null}
          theme="light"
          className={`app-sider${sider.expanded ? '' : ' app-sider-rail'}${sider.floating ? ' app-sider-floating' : ''}`}
          onMouseEnter={sider.collapsed ? sider.onMouseEnter : undefined}
          onMouseLeave={sider.collapsed ? sider.onMouseLeave : undefined}
        >
          <div className="app-sider-inner">
            <SidebarBrand
              companyName={companyName}
              title={settings.data?.brandTitle}
              subtitle={settings.data?.brandSubtitle}
              logoFileId={settings.data?.brandLogoFileId}
              iconFileId={settings.data?.brandFaviconFileId}
            />
            <div className="app-sider-menu">
              <Menu mode="inline" items={items} selectedKeys={selected} style={{ borderInlineEnd: 0 }} />
            </div>
            <div className="app-sider-footer">
              <Tooltip title={sider.expanded ? undefined : 'Expand sidebar'} placement="right">
                <Button
                  type="text"
                  block
                  aria-label={sider.collapsed ? 'Keep sidebar open' : 'Collapse sidebar'}
                  icon={sider.collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  className="app-sider-footer-btn"
                  onClick={sider.toggle}
                >
                  <span className="app-sider-label">{sider.collapsed ? 'Keep open' : 'Collapse'}</span>
                </Button>
              </Tooltip>
              <Tooltip title={sider.expanded ? undefined : 'Sign out'} placement="right">
                <Button type="text" danger block aria-label="Sign out" icon={<LogoutOutlined />} className="app-sider-footer-btn" onClick={() => void logout()}>
                  <span className="app-sider-label">Sign out</span>
                </Button>
              </Tooltip>
            </div>
          </div>
        </Sider>
      </div>
      <Layout className="app-main">
        <Header className="app-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <GlobalSearch />
          </div>
          <Space size={8} align="center">
            <ProjectSwitcher />
            <Tooltip title="Settings">
              <Button type="text" shape="circle" size="large" aria-label="Settings" icon={<SettingOutlined />} onClick={() => navigate('/settings')} />
            </Tooltip>
            {inboxOn && (
              <Tooltip title={unread ? `${unread} unread in the inbox` : 'Inbox'}>
                <Badge dot={unread > 0} offset={[-8, 8]}>
                  <Button type="text" shape="circle" size="large" aria-label="Inbox notifications" icon={<BellOutlined />} onClick={() => navigate('/inbox')} />
                </Badge>
              </Tooltip>
            )}
            <Divider orientation="vertical" style={{ height: 28, margin: '0 8px' }} />
            <Dropdown menu={{ items: userMenu }} trigger={['click']} placement="bottomRight">
              <Button type="text" aria-label="Account menu" style={{ height: 48, padding: '0 4px', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <Avatar size={40} src={fileUrl(user?.photoFileId)} style={{ background: '#EEF2FE', color: '#2F5BEA', fontWeight: 600 }}>
                  {user?.name?.[0]?.toUpperCase()}
                </Avatar>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                  <Typography.Text strong>{user?.name}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {user?.role === 'SUPER_ADMIN' ? 'Super admin' : 'Admin'}
                  </Typography.Text>
                </span>
              </Button>
            </Dropdown>
          </Space>
        </Header>
        <Content className="app-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
    </ProjectScopeProvider>
  );
}
